import { useState, useEffect } from 'react';
import { Select, Space, Tag, Typography, Divider } from 'antd';
import { UserOutlined, TeamOutlined } from '@ant-design/icons';
import SDIModal from '../../../../components/ui/SDIModal';
import axiosInstance from '../../../../variables/api/axiosInstance';
import { toast } from 'react-toastify';
import { LOGGED_USER } from '../../../../variables/constants';

const { Text } = Typography;

const ChangementProducteur = (props) => {
    const [loading, setLoading] = useState(false);
    const [utilisateurs, setUtilisateurs] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState(null);

    useEffect(() => {
        if (!props.open) return;
        setSelectedUserId(props.contrat?.producteur?.codeId || null);
        const codeOrganisme =
            props.contrat?.organisme?.code ||
            LOGGED_USER?.profil?.organisme?.code;
        if (!codeOrganisme) return;
        setLoadingUsers(true);
        axiosInstance
            .get('/api/utilisateur', {
                params: {
                    page: 0,
                    size: 500,
                    organisme: codeOrganisme,
                    activeData: true,
                },
            })
            .then((res) => {
                const list = Array.isArray(res.data?.content)
                    ? res.data.content
                    : [];
                setUtilisateurs(list);
            })
            .catch(() => toast.error('Erreur lors du chargement des utilisateurs'))
            .finally(() => setLoadingUsers(false));
    }, [props.open]);

    const handleSubmit = () => {
        if (!selectedUserId) {
            toast.error('Veuillez sélectionner un producteur');
            return;
        }
        const currentId = props.contrat?.producteur?.codeId || null;
        if (selectedUserId === currentId) {
            toast.info('Ce producteur est déjà associé au contrat');
            return;
        }
        setLoading(true);
        axiosInstance
            .put(
                '/api/contrat/changement-producteur',
                JSON.stringify({
                    codeContrat: props.contrat?.codeId,
                    producteurCodeId: selectedUserId,
                })
            )
            .then(() => {
                toast.success('Producteur modifié avec succès');
                props.update(false);
                window.location.reload();
            })
            .catch((err) => {
                const msg =
                    err?.response?.data?.message ||
                    'Erreur lors de la modification du producteur';
                toast.error(msg);
            })
            .finally(() => setLoading(false));
    };

    const currentProducteurId = props.contrat?.producteur?.codeId;

    // Tri : producteur actuel en premier, puis agents (hors SIEGE), puis SIEGE
    const sorted = [...utilisateurs].sort((a, b) => {
        if (a.codeId === currentProducteurId) return -1;
        if (b.codeId === currentProducteurId) return 1;
        const aIsAgent =
            a.agence?.typeAgence && a.agence?.typeAgence !== 'SIEGE';
        const bIsAgent =
            b.agence?.typeAgence && b.agence?.typeAgence !== 'SIEGE';
        if (aIsAgent && !bIsAgent) return -1;
        if (!aIsAgent && bIsAgent) return 1;
        return (a.personne?.nom || '').localeCompare(
            b.personne?.nom || '',
            'fr'
        );
    });

    const options = sorted.map((user) => {
        const isCurrent = user.codeId === currentProducteurId;
        const isAgent =
            user.agence?.typeAgence && user.agence?.typeAgence !== 'SIEGE';
        const nom =
            `${user.personne?.nom || ''} ${user.personne?.prenom || ''}`.trim();
        const agenceLabel = user.agence?.nom ? ` — ${user.agence.nom}` : '';
        return {
            value: user.codeId,
            // label utilisé pour le filtrage texte
            label: `${nom}${agenceLabel}${isCurrent ? ' (Actuel)' : ''}`,
            isCurrent,
            isAgent,
            user,
        };
    });

    return (
        <SDIModal
            title="CHANGEMENT DE PRODUCTEUR"
            confirmLoading={loading}
            centered
            open={props.open}
            onOk={handleSubmit}
            onCancel={() => props.update(false)}
            okText="Confirmer le changement"
            cancelText="Annuler"
            width={680}
            zIndex={1500}
        >
            {props.contrat?.producteur && (
                <>
                    <div
                        style={{
                            padding: '12px 16px',
                            background: '#f0f5ff',
                            borderRadius: 8,
                            border: '1px solid #adc6ff',
                            marginBottom: 20,
                        }}
                    >
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Producteur actuel
                        </Text>
                        <br />
                        <Text strong style={{ fontSize: 15 }}>
                            <UserOutlined style={{ marginRight: 6 }} />
                            {props.contrat?.producteur?.personne?.nom}{' '}
                            {props.contrat?.producteur?.personne?.prenom}
                        </Text>
                        {props.contrat?.producteur?.agence?.nom && (
                            <Text type="secondary">
                                {' '}
                                — {props.contrat?.producteur?.agence?.nom}
                            </Text>
                        )}
                    </div>
                    <Divider style={{ margin: '0 0 16px 0' }} />
                </>
            )}

            <div>
                <Text strong>
                    <TeamOutlined style={{ marginRight: 6 }} />
                    Sélectionner le nouveau producteur
                </Text>
                <Select
                    showSearch
                    style={{ width: '100%', marginTop: 8 }}
                    placeholder="Rechercher un utilisateur..."
                    value={selectedUserId || undefined}
                    onChange={setSelectedUserId}
                    loading={loadingUsers}
                    optionFilterProp="label"
                    size="large"
                    labelRender={(labelProps) => {
                        // Quand l'option correspondante est chargée, antd fournit déjà
                        // son label (nom + agence). Sinon (liste pas encore chargée ou
                        // producteur absent de la liste), on affiche le nom du producteur
                        // actuel du contrat plutôt que son code brut.
                        if (labelProps.label) return labelProps.label;
                        const p = props.contrat?.producteur;
                        if (labelProps.value === currentProducteurId && p) {
                            const nom = `${p.personne?.nom || ''} ${p.personne?.prenom || ''}`.trim();
                            const agence = p.agence?.nom ? ` — ${p.agence.nom}` : '';
                            return `${nom}${agence}` || labelProps.value;
                        }
                        return labelProps.value;
                    }}
                    optionRender={(option) => {
                        const { isCurrent, isAgent, user } = option.data;
                        const nom = `${user.personne?.nom || ''} ${user.personne?.prenom || ''}`.trim();
                        return (
                            <Space>
                                {isCurrent && (
                                    <Tag color="blue" style={{ fontSize: 11 }}>
                                        Actuel
                                    </Tag>
                                )}
                                {isAgent && !isCurrent && (
                                    <Tag color="green" style={{ fontSize: 11 }}>
                                        Agent
                                    </Tag>
                                )}
                                <UserOutlined />
                                <span style={{ fontWeight: isCurrent ? 600 : 400 }}>
                                    {nom}
                                </span>
                                {user.agence?.nom && (
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        — {user.agence.nom}
                                    </Text>
                                )}
                            </Space>
                        );
                    }}
                    options={options}
                />
            </div>
        </SDIModal>
    );
};

export default ChangementProducteur;
